/**
 * @Author        : zhouming
 * @页面名称          : HTTP 请求模块 (无UI依赖版本)
 * @Description   : 基于依赖注入的 HTTP 客户端，实现 UI 层解耦
 *
 * 🎯 设计理念:
 * ============================================
 * 1. **关注点分离**: HTTP 客户端不应该直接依赖具体的 UI 组件库
 * 2. **依赖注入**: 通过 MessageHandler 接口注入错误处理逻辑
 * 3. **可测试性**: 便于单元测试和 Mock 错误处理行为
 * 4. **灵活扩展**: 支持多种运行环境 (Web/Node.js/React Native)
 * 5. **向后兼容**: 不影响现有代码，支持渐进式迁移
 *
 * 🏗️ 架构优势:
 * ============================================
 * - Web 环境: 可使用 antd.message / react-hot-toast / 原生 alert
 * - 服务端渲染: 可使用 console.log / 日志系统
 * - 移动端: 可使用原生弹窗 / Toast 组件
 * - 测试环境: 可使用 Mock 处理器进行测试
 *
 * @see https://www.axios-http.cn/docs/interceptors
 */
import type {
    TypeInterceptorHooks,
    TypeExpandAxiosResponse,
    TypeExpandAxiosRequestConfig,
    TypeBaseApiResponse,
} from '@/types/http';
import type {AxiosInstance, AxiosResponse} from 'axios';

import axios from 'axios';

// 定义消息处理器接口
export interface MessageHandler {
    /** 错误消息处理 (必需) */
    error(message: string): void;
    /** 成功消息处理 (可选) */
    success?(message: string): void;
}

// 默认的控制台处理器
const defaultMessageHandler: MessageHandler = {
    error: (msg: string) => console.error('[HTTP Error]:', msg),
    success: (msg: string) => console.log('[HTTP Success]:', msg),
};

// 请求成功码
const SUCCESS_CODE = 0;
// 未登录
const NOT_LOGGED_IN_CODE = 700;
// 这里用来处理 http 常见错误，进行全局提示
const mapErrorStatus = new Map([
    [400, '请求方式错误'],
    [401, '请重新登录'],
    [403, '拒绝访问'],
    [404, '请求地址有误'],
    [500, '服务器出错'],
]);

class Request {
    // axios 实例
    private _instance: AxiosInstance;

    // 消息处理器
    private _messageHandler: MessageHandler;

    // 默认配置
    private _defaultConfig: TypeExpandAxiosRequestConfig = {
        // baseURL: '/api',
        timeout: 5000,
        requestOptions: {
            globalErrorMessage: true,
            globalSuccessMessage: false,
        },
        withCredentials: true,
    };

    private _interceptorHooks?: TypeInterceptorHooks;

    constructor(config: TypeExpandAxiosRequestConfig, messageHandler: MessageHandler = defaultMessageHandler) {
        // 使用axios.create创建axios实例
        this._instance = axios.create(Object.assign(this._defaultConfig, config));
        this._messageHandler = messageHandler;
        this._interceptorHooks = config.interceptorHooks;
        this.setupInterceptors();
    }

    // 定义核心请求
    request(config: TypeExpandAxiosRequestConfig): Promise<AxiosResponse> {
        // ！！！⚠️ 注意：axios 已经将请求使用 promise 封装过了
        // 这里直接返回，不需要我们再使用 promise 封装一层
        return this._instance.request(config);
    }

    get<T = any>(url: string, config?: TypeExpandAxiosRequestConfig): Promise<TypeBaseApiResponse<T>> {
        return this._instance.get(url, config);
    }

    post<T = any>(url: string, data?: any, config?: TypeExpandAxiosRequestConfig): Promise<TypeBaseApiResponse<T>> {
        return this._instance.post(url, data, config);
    }

    put<T = any>(url: string, data?: any, config?: TypeExpandAxiosRequestConfig): Promise<TypeBaseApiResponse<T>> {
        return this._instance.put(url, data, config);
    }

    delete<T = any>(url: string, config?: TypeExpandAxiosRequestConfig): Promise<TypeBaseApiResponse<T>> {
        return this._instance.delete(url, config);
    }

    // 通用拦截，在初始化时就进行注册和运行，对基础属性进行处理
    private setupInterceptors() {
        // 创建带有消息处理器的拦截器
        const interceptorHooks: TypeInterceptorHooks = {
            requestInterceptor:
                this._interceptorHooks?.requestInterceptor ||
                ((config) => {
                    // 请求头部处理，如添加 token
                    // const token = 'token-value';
                    // if (token) {
                    //     config!.headers!.Authorization = token;
                    // }
                    // 背景: 配置了 cookie 的获取逻辑，就是request's credentials mode is 'include'，这个规则要求对应域名 Access-Control-Allow-Origin 也指定请求来源的域名，否则就会 block。
                    // 需要网关返回 200 而不是 302 此字段关闭网关重定向
                    config.headers['X-Cas-Redirect-Switch'] = 'disable';
                    return config;
                }),

            requestInterceptorCatch:
                this._interceptorHooks?.requestInterceptorCatch ||
                ((err) => {
                    // 请求错误，这里可以用全局提示框进行提示
                    return Promise.reject(err);
                }),

            responseInterceptor:
                this._interceptorHooks?.responseInterceptor ||
                ((result) => {
                    // 因为 axios 返回不支持扩展自定义配置，需要自己断言一下
                    const res = result as TypeExpandAxiosResponse;
                    const {data} = res;

                    if (data instanceof Blob) {
                        return data;
                    }

                    // 700 未登录 自动跳转
                    if (data?.errorCode === NOT_LOGGED_IN_CODE || data?.code === NOT_LOGGED_IN_CODE) {
                        if (location.host.includes('localhost')) {
                            this._messageHandler.error('700 登陆失败, 需要重新登陆 (仅 localhost)');
                            return;
                        }

                        window.location.href = data?.location;
                        return;
                    }

                    // 兜底异常
                    if (data.errorCode !== SUCCESS_CODE) {
                        if (res.config.requestOptions?.globalErrorMessage) {
                            const msg = res?.data?.error || res?.data?.message || '请求出错';

                            // 使用注入的消息处理器
                            this._messageHandler.error(msg);
                            console.error('接口异常状态 2:', msg);
                        }
                        return Promise.reject(res.data);
                    }

                    if (res.config.requestOptions?.globalSuccessMessage) {
                        // 这里全局提示请求成功
                        const msg = res?.data?.message || '请求成功';
                        this._messageHandler.success?.(msg);
                    }

                    // 请求返回值，建议将 返回值 进行解构
                    return data;
                }),

            responseInterceptorCatch:
                this._interceptorHooks?.responseInterceptorCatch ||
                ((err) => {
                    const msg =
                        mapErrorStatus.get(err?.response?.status) || err?.response?.message || '请求出错，请稍后再试';
                    // 使用注入的消息处理器
                    this._messageHandler.error(msg);
                    console.error('接口异常状态 1:', msg);

                    return Promise.reject(err?.response || err);
                }),
        };

        this._instance.interceptors.request.use(
            // 在发送请求之前做些什么
            interceptorHooks.requestInterceptor,
            // 对请求错误做些什么
            interceptorHooks.requestInterceptorCatch,
        );
        this._instance.interceptors.response.use(
            interceptorHooks.responseInterceptor,
            interceptorHooks.responseInterceptorCatch,
        );
    }
}

// 导出 Request 类 (MessageHandler 接口已在上面导出)
export {Request};

// 🚫 故意不提供默认实例，强制用户传入消息处理器
// 这避免了隐式依赖，提高了代码的可维护性和可测试性
